// src/features/groups/components/GroupActions.tsx

import React, { useState } from 'react';
import {
    useTranslate,
    useRedirect,
} from 'react-admin';
import {
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Tooltip,
    Box,
} from '@mui/material';
import {
    MoreVert as MoreVertIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import { useGroupPermissions } from '../hooks/useGroupPermissions';
import { Group } from '../types';
import BaseDeleteDialog from '../../../components/common/BaseDeleteDialog';

interface GroupActionsProps {
    record: Group;
    small?: boolean;
}

const GroupActions: React.FC<GroupActionsProps> = ({ record, small = false }) => {
    const translate = useTranslate();
    const redirect = useRedirect();
    
  
    const permissions = useGroupPermissions(record);
    
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleEdit = () => {
        handleMenuClose();
        redirect(`/groups/${record.id}`);
    };

    const handleDeleteClick = () => {
        handleMenuClose();
        setShowDeleteDialog(true);
    };

    const handleDeleteClose = () => {
        setShowDeleteDialog(false);
    };

    const handleDeleteConfirm = () => {
        setShowDeleteDialog(false);
    };

    
    const deleteMessage = record.hotelCount && record.hotelCount > 0
        ? translate(
            'resources.groups.dialogs.delete_warning_with_hotels',
            { name: record.name, count: record.hotelCount }
          )
        : translate(
            'resources.groups.dialogs.delete_message',
            { name: record.name }
          ); 
    if (small) {
        return (
            <Box sx={{ display: 'flex', gap: 1 }}>
                {permissions.canEditThisGroup && (
                    <Tooltip title={translate('ra.action.edit')}>
                        <IconButton size="small" onClick={handleEdit}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {permissions.canDeleteThisGroup && (
                    <Tooltip title={translate('ra.action.delete')}>
                        <IconButton size="small" onClick={handleDeleteClick}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                
                {showDeleteDialog && (
                    <BaseDeleteDialog
                        open={showDeleteDialog}
                        onClose={handleDeleteClose}
                        onConfirm={handleDeleteConfirm}
                        resource="groups"
                        recordId={record.id}
                        record={record}
                        title={translate('resources.groups.dialogs.delete_title', 'Eliminar Grupo')}
                        message={deleteMessage}
                        warningMessage={
                            record.hotelCount && record.hotelCount > 0
                                ? translate(
                                    'resources.groups.dialogs.delete_cascade_warning',
                                    'Esta acción afectará a los hoteles asociados.'
                                )
                                : undefined
                        }
                    />
                )}
            </Box>
        );
    }

   
    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Tooltip title={translate('ra.action.more', 'Más acciones')}>
                    <IconButton
                        onClick={handleMenuOpen}
                        size="small"
                         sx={{ color: 'action.active' }}
                    >
                        <MoreVertIcon />
                    </IconButton>
                </Tooltip>
            </Box>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                {permissions.canEditThisGroup && (
                    <MenuItem onClick={handleEdit}>
                        <ListItemIcon>
                            <EditIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>
                            {translate('ra.action.edit', 'Editar')}
                        </ListItemText>
                    </MenuItem>
                )}

            

                {/* Separador si hay más acciones */}
                {(permissions.canEditThisGroup || permissions.canDeleteThisGroup) && (
                    <MenuItem divider />
                )}


                {permissions.canDeleteThisGroup && (
                    <MenuItem onClick={handleDeleteClick}>
                        <ListItemIcon>
                            <DeleteIcon fontSize="small" color="error" />
                        </ListItemIcon>
                        <ListItemText sx={{ color: 'error.main' }}>
                            {translate('ra.action.delete', 'Eliminar')}
                        </ListItemText>
                    </MenuItem>
                )}
            </Menu>

            {showDeleteDialog && (
                <BaseDeleteDialog
                    open={showDeleteDialog}
                    onClose={handleDeleteClose}
                    onConfirm={handleDeleteConfirm}
                    resource="groups"
                    recordId={record.id}
                    record={record}
                    title={translate('resources.groups.dialogs.delete_title', 'Eliminar Grupo')}
                    message={deleteMessage}
                    warningMessage={
                        record.hotelCount && record.hotelCount > 0
                            ? translate(
                                'resources.groups.dialogs.delete_cascade_warning',
                                'Esta acción afectará a los hoteles asociados.'
                            )
                            : undefined
                    }
                />
            )}
        </>
    );
};

export default GroupActions;